import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, ArrowRight, Clock, Calendar, Tag as TagIcon, Search, Sparkles } from "lucide-react";
import { BLOG_POSTS, CATEGORIES } from "@/data/blogPosts";
import { useSeo } from "@/lib/useSeo";
import NewsletterForm from "@/components/NewsletterForm";
import LanguageHelper from "@/components/LanguageHelper";

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [query, setQuery] = useState("");

  useSeo({
    title: "Blog Student Hub — Guides pratiques pour étudiants en France (2026)",
    description: "Articles, guides et astuces pour réussir ta vie étudiante en France : APL, CROUS, alternance, logement, mutuelle, banque, Erasmus. Mis à jour régulièrement par l'équipe Student Hub.",
    canonical: "https://studenthubfrance.fr/blog",
    ogImage: "https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?auto=format&fit=crop&w=1200&q=85",
    lang: "fr",
    hreflangs: [
      { lang: "fr", url: "https://studenthubfrance.fr/blog" },
      { lang: "x-default", url: "https://studenthubfrance.fr/blog" },
    ],
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Blog Student Hub",
      url: "https://studenthubfrance.fr/blog",
      description: "Articles, guides et astuces pour réussir ta vie étudiante en France.",
      inLanguage: "fr-FR",
      publisher: {
        "@type": "Organization",
        name: "Student Hub",
        url: "https://studenthubfrance.fr",
        logo: { "@type": "ImageObject", url: "https://studenthubfrance.fr/logo.png" },
      },
      blogPost: BLOG_POSTS.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `https://studenthubfrance.fr/blog/${p.slug}`,
        datePublished: p.date,
        dateModified: p.updatedAt || p.date,
        author: { "@type": "Organization", name: p.author || "Équipe Student Hub" },
        description: p.description,
        image: p.cover,
        keywords: p.keywords,
      })),
    },
  });

  const sortedPosts = useMemo(() => [...BLOG_POSTS].sort((a, b) => b.date.localeCompare(a.date)), []);

  const filtered = useMemo(() => {
    let list = sortedPosts;
    if (activeCategory !== "all") list = list.filter((p) => p.category === activeCategory);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        (p.keywords || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [sortedPosts, activeCategory, query]);

  // Breadcrumb JSON-LD
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://studenthubfrance.fr/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://studenthubfrance.fr/blog" },
    ],
  };

  React.useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(breadcrumbLd);
    el.dataset.seoHook = "1";
    document.head.appendChild(el);
    return () => el.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-white" data-testid="blog-page">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-violet-100 blur-3xl opacity-50" />
      </div>

      <header className="relative z-40 max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="blog-logo">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center shadow-lg shadow-violet-500/30">
            <GraduationCap size={18} />
          </div>
          <span className="font-display text-lg font-bold text-slate-900 hidden sm:inline">Student Hub</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <LanguageHelper />
          <Link to="/landing/etudiants" className="hidden sm:inline text-sm font-medium text-slate-700 hover:text-violet-600 transition-colors">L'app</Link>
          <Link to="/register" className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors" data-testid="blog-register-cta">
            Commencer <ArrowRight size={14} />
          </Link>
        </nav>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-500 pt-4" aria-label="Fil d'Ariane" data-testid="blog-breadcrumb">
          <Link to="/" className="hover:text-violet-600">Accueil</Link>
          <span className="mx-2">/</span>
          <span className="text-slate-700 font-medium">Blog</span>
        </nav>

        <section className="pt-6 sm:pt-10 mb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-violet-600 font-semibold mb-3">Blog · Conseils étudiants</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
            Guides pratiques pour la vie étudiante
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg">
            APL, CROUS, alternance, mutuelle, comptes bancaires, étudiants internationaux… Articles à jour 2026, rédigés par <strong>l'équipe Student Hub</strong>, sans bullshit.
          </p>
        </section>

        {/* Search + categories */}
        <section className="sticky top-0 z-30 -mx-5 sm:-mx-8 px-5 sm:px-8 py-3 bg-white/90 backdrop-blur border-b border-slate-100 mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher : APL, CROUS, Erasmus…"
                className="w-full pl-9 pr-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                data-testid="blog-search"
                aria-label="Rechercher un article"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3" role="tablist" data-testid="blog-categories">
            <CatChip active={activeCategory === "all"} onClick={() => setActiveCategory("all")} testid="cat-all">
              Tous
            </CatChip>
            {Object.entries(CATEGORIES).map(([key, cat]) => (
              <CatChip
                key={key}
                active={activeCategory === key}
                onClick={() => setActiveCategory(key)}
                gradient={cat.color}
                testid={`cat-${key}`}
              >
                {cat.label}
              </CatChip>
            ))}
          </div>
        </section>

        {filtered.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center" data-testid="blog-empty">
            <p className="text-slate-500">Aucun article ne correspond à ta recherche.</p>
            <button onClick={() => { setQuery(""); setActiveCategory("all"); }} className="mt-3 text-sm text-violet-600 hover:underline">
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-list">
            {filtered.map((post, idx) => {
              const cat = CATEGORIES[post.category];
              const isFeatured = idx === 0 && activeCategory === "all" && !query;
              return (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className={`group relative rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-violet-300 hover:shadow-xl transition-all ${isFeatured ? "md:col-span-2" : ""}`}
                  data-testid={`blog-card-${post.slug}`}
                >
                  <div className={`relative ${isFeatured ? "aspect-[2.4/1]" : "aspect-[16/9]"} overflow-hidden bg-slate-100`}>
                    <img
                      src={post.cover}
                      alt={post.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className={`absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${cat.color} text-white text-[10px] font-bold uppercase tracking-wide shadow-md`}>
                      <TagIcon size={10} /> {cat.label}
                    </span>
                    {isFeatured && (
                      <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-violet-700 text-[10px] font-bold uppercase tracking-wide shadow">
                        <Sparkles size={10} /> À la une
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <h2 className={`font-display font-bold text-slate-900 leading-tight group-hover:text-violet-700 transition-colors ${isFeatured ? "text-2xl sm:text-3xl" : "text-lg"}`}>
                      {post.title}
                    </h2>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{post.excerpt}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar size={12} /> {new Date(post.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> {post.readTime} min</span>
                      <span className="text-emerald-600 font-medium inline-flex items-center gap-1">✓ Vérifié</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </section>
        )}

        <section className="mt-16">
          <NewsletterForm source="blog-index" variant="card" />
        </section>

        <section className="mt-10 rounded-3xl bg-gradient-to-br from-blue-600 via-violet-600 to-fuchsia-600 p-8 sm:p-12 text-white text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Tu veux passer à l'action ?</h2>
          <p className="text-violet-100 mt-2 max-w-xl mx-auto text-sm sm:text-base">
            Tous ces guides sont reliés à des outils concrets dans l'app. Crée ton compte gratuit pour suivre tes démarches en un clic.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 font-bold shadow-xl hover:bg-slate-100 transition-colors"
            data-testid="blog-footer-cta"
          >
            Créer mon compte gratuit <ArrowRight size={16} />
          </Link>
        </section>
      </main>

      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Student Hub · <Link to="/legal/cgu" className="hover:text-violet-600">CGU</Link> · <Link to="/legal/confidentialite" className="hover:text-violet-600">Confidentialité</Link> · <Link to="/securite" className="hover:text-violet-600">Sécurité</Link>
      </footer>
    </div>
  );
}

function CatChip({ active, onClick, gradient, testid, children }) {
  return (
    <button
      onClick={onClick}
      role="tab"
      aria-selected={active}
      data-testid={testid}
      className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors border ${
        active
          ? gradient
            ? `bg-gradient-to-r ${gradient} text-white border-transparent shadow-sm`
            : "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-700 border-slate-200 hover:border-violet-300 hover:text-violet-700"
      }`}
    >
      {children}
    </button>
  );
}
